import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { DatePipe } from '@angular/common';
import { RouterModule } from '@angular/router';
import { ConversionService } from '@cosmos/angular/utils';
import { Device, LoraMetadata } from '@cosmos/shared';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DividerModule } from 'primeng/divider';
import { PanelModule } from 'primeng/panel';

// TODO: consider making specific cards for different "Gateway" deviceTypes
@Component({
  selector: 'cosmos-gw-card',
  imports: [TranslatePipe, DatePipe, RouterModule,
    CardModule, ButtonModule, DividerModule, PanelModule,
  ],
  templateUrl: './gw-card.component.html',
  styleUrls: ['./gw-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GwCardComponent {
  private readonly conversionService = inject(ConversionService);

  readonly gateway = input.required<Device>();
  readonly showNavigation = input(false);
  readonly extended = input(false);
  readonly project = input(false);
  readonly connection = input(false);
  /** Explicit project context for the "Details" link. Callers that already know
   *  the project (e.g. the project-scoped devices page) pass it; otherwise it
   *  falls back to `gateway.project`. Empty ⇒ the nav footer is hidden. */
  readonly projectId = input<string>('');

  readonly resolvedProjectId = computed(() => {
    if (this.projectId()) return this.projectId();
    const p = this.gateway().project as { _id?: string } | string | undefined;
    return (typeof p === 'string' ? p : p?._id) ?? '';
  });

  /** Transmission history newest-first, or `null` when there is none — lets the
   *  template narrow with `@if (…; as transmissions)` instead of repeatedly
   *  indexing a possibly-undefined `gateway().transmission`. */
  readonly latestTransmissions = computed<LoraMetadata[] | null>(() => {
    const t = this.gateway().transmission;
    return t && t.length > 0 ? [...t].reverse() : null;
  });

  getTransmissionDetails(transmission: LoraMetadata) {
    return this.conversionService.getTransmissionInfo(transmission);
  }

  timeComparison(gateway: Device) {
    const now = new Date();
    const lastSeen = gateway.lastSeenAt || gateway.chirpstackMetadata?.lastSeenAt;
    if (!lastSeen) return 9999999999;
    return now.valueOf() - new Date(lastSeen).valueOf();
  }
}
